import React from 'react';
import { Box, Typography, useTheme, alpha } from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import GlassCard from './GlassCard';

const ActionButton = styled(motion.div)(({ theme }) => ({
  cursor: 'pointer',
  width: '100%',
  minWidth: 200,
  position: 'relative',
  
  [theme.breakpoints.down('sm')]: {
    minWidth: 'auto',
    width: '100%'
  }
}));

const QuickActionButton = ({ 
  icon: Icon, 
  title, 
  description, 
  onClick, 
  color = '#6366F1',
  delay = 0,
  shortcut
}) => {
  const theme = useTheme();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ 
        duration: 0.5, 
        delay,
        type: "spring",
        stiffness: 120
      }}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <ActionButton
        onClick={onClick}
        whileHover={{ y: -2 }}
        transition={{ type: "spring", stiffness: 400, damping: 25 }}
      >
        <GlassCard 
          variant="action"
          sx={{ 
            height: '100%',
            minHeight: 120,
            position: 'relative',
            overflow: 'hidden'
          }}
          contentSx={{ 
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            gap: 1.5,
            position: 'relative',
            zIndex: 2
          }}
        >
          {/* Background gradient */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `radial-gradient(circle at 50% 50%, ${alpha(color, 0.1)} 0%, transparent 70%)`,
              zIndex: 1,
            }}
          />

          {/* Icon container */}
          <motion.div
            whileHover={{ rotate: 5, scale: 1.1 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 48,
                height: 48,
                borderRadius: '12px',
                background: `linear-gradient(135deg, ${color}25, ${color}15)`,
                border: `1px solid ${alpha(color, 0.3)}`,
                position: 'relative',
                zIndex: 2,
                
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  inset: -1,
                  borderRadius: '12px',
                  background: `linear-gradient(135deg, ${color}40, transparent)`,
                  mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                  maskComposite: 'xor',
                  WebkitMaskComposite: 'xor',
                  opacity: 0,
                  transition: 'opacity 0.3s ease',
                },
                
                '&:hover::before': {
                  opacity: 1,
                }
              }}
            >
              <Icon sx={{ fontSize: 24, color }} />
            </Box>
          </motion.div>

          {/* Content */}
          <Box sx={{ position: 'relative', zIndex: 2 }}>
            <Typography 
              variant="h6" 
              fontWeight="600"
              sx={{ 
                color: theme.palette.text.primary,
                fontSize: '1rem',
                mb: 0.5
              }}
              dangerouslySetInnerHTML={{ __html: title }}
            />
            
            {description && (
              <Typography 
                variant="body2" 
                sx={{ 
                  color: theme.palette.text.secondary,
                  fontSize: '0.8rem',
                  lineHeight: 1.3
                }}
              >
                {description}
              </Typography>
            )}
          </Box>

          {/* Keyboard shortcut */}
          {shortcut && (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                px: 1,
                py: 0.5,
                borderRadius: '6px',
                background: alpha(theme.palette.background.paper, 0.6),
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                zIndex: 3
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  fontFamily: 'monospace'
                }}
              >
                {shortcut}
              </Typography>
            </Box>
          )}

          {/* Hover effect overlay */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `linear-gradient(135deg, ${alpha(color, 0.05)} 0%, transparent 100%)`,
              opacity: 0,
              transition: 'opacity 0.3s ease',
              zIndex: 1,
              
              '.MuiCard-root:hover &': {
                opacity: 1,
              }
            }}
          />
        </GlassCard>
      </ActionButton>
    </motion.div>
  );
};

export default QuickActionButton;
